import {
  ArrowPathIcon,
  ArrowPathRoundedSquareIcon,
  ClipboardIcon,
} from "@heroicons/react/24/outline";
import ReactTooltip from "react-tooltip";
import type { ReportSummaryItemApi } from "../../api/Reports";
import Badge from "../../shared-components/Badge";
import NotLoadedYet from "../../shared-components/NotLoadedYet";
import TableReportDateWithFormat from "../../shared-components/TableReportDateWithFormat";
import MisconductCancelButton from "../MisconductCancelButton";
import ReportStatus from "../ReportStatus";
import ReportStepsModal from "../ReportStepsModal";
import { reportFailed } from "../utils";
import { formattedISODate } from "../../utils";
import {
  ReportEntryStyle,
  ReportRowItem,
} from "../helper-components/TableModels";

export type InternalIqaV2026RowOpts = {
  stepLoading?: boolean;
  canInternalQaEdit: boolean;
  canCancelMisconduct: boolean;
  navigateWithState: (reportUUID: string, page: string) => void;
};

function internalIqaStatusCell(
  report: ReportSummaryItemApi,
  stepLoading: boolean
) {
  if (stepLoading) {
    return (
      <>
        <NotLoadedYet className="h-6 w-[12ch]" />
        <ReportStepsModal workflowRunUUID={report.workflow_run_uuid} />
      </>
    );
  }

  const stepName = reportFailed(report) ? null : report.step_name || null;

  return (
    <>
      <ReportStatus
        stepStatusName={stepName}
        failed={reportFailed(report)}
        report={report}
        allowResumeOnClick={true}
      />
      <ReportStepsModal workflowRunUUID={report.workflow_run_uuid} />
    </>
  );
}

/**
 * Single Internal QA row when data comes from ``GET /lists/misconduct/internal_qa``.
 * Per-row step via ``GET /workflow_runs/{uuid}/latest_step_run`` (FAILED rows skip the fetch).
 */
export function buildInternalIqaV2026TableRow(
  report: ReportSummaryItemApi,
  opts: InternalIqaV2026RowOpts
): ReportRowItem<ReportSummaryItemApi> {
  const {
    stepLoading = false,
    canInternalQaEdit,
    canCancelMisconduct,
    navigateWithState,
  } = opts;

  const data = [
    {
      text: report.full_name,
      style: ReportEntryStyle.COMPONENT,
      component: (
        <div className="flex">
          <div>
            {canInternalQaEdit ? (
              <span
                onClick={() => navigateWithState(report.report_uuid, "iqa")}
                className="font-semibold underline cursor-pointer"
              >
                {report.full_name}
              </span>
            ) : (
              <span className="font-semibold">{report.full_name}</span>
            )}
            <small className="block font-normal">{report.company_name}</small>
          </div>
          <div>
            {(report.step_payload?.rerun_from_parent_uuid || report.has_rerun) && (
              <div>
                <ArrowPathIcon className="w-5 ml-2 text-blue-600" />
              </div>
            )}
          </div>
        </div>
      ),
    },
    {
      text: report.report_status,
      style: ReportEntryStyle.COMPONENT,
      component: internalIqaStatusCell(report, stepLoading),
      customClass: "flex-1 w-4",
    },
    {
      text: report.was_qa ? "yes" : "no",
      style: ReportEntryStyle.COMPONENT,
      component:
        report.was_qa === undefined || report.was_qa === null ? (
          <Badge color="gray">N/A</Badge>
        ) : report.was_qa ? (
          <Badge color="green">Yes</Badge>
        ) : (
          <Badge color="red">No</Badge>
        ),
    },
    {
      component: (
        <div className="flex flex-col">
          <span className="border-b-2">{report.blueprint_name}</span>{" "}
          <small className="block">{report.behavior_kit_name ?? "General"}</small>
        </div>
      ),
      text: report.blueprint_name + " " + (report.behavior_kit_name ?? "General"),
      style: ReportEntryStyle.COMPONENT,
    },
    {
      text: formattedISODate(report.report_date_added),
      component: report.recur ? (
        <>
          <TableReportDateWithFormat
            date={report.report_date_added}
            updated={false}
            unique_id={report.report_uuid}
          />
          <span
            data-tip="Employee Screening"
            data-tooltip-id={`recur-tip-${report.report_uuid}`}
            className="cursor-pointer"
          >
            <ArrowPathRoundedSquareIcon className="w-5 text-gray-500" />
          </span>
          <ReactTooltip id={`recur-tip-${report.report_uuid}`} />
        </>
      ) : (
        <TableReportDateWithFormat
          date={report.report_date_added}
          updated={false}
        />
      ),
      style: ReportEntryStyle.COMPONENT,
      customClass: "flex-1 whitespace-pre",
    },
    {
      text: formattedISODate(report.due_date),
      component: (
        <TableReportDateWithFormat
          date={report.due_date}
          updated={report.due_date_updated}
        />
      ),
      style: ReportEntryStyle.COMPONENT,
      customClass: "flex-1 whitespace-pre",
    },
    {
      text: report.report_uuid,
      style: ReportEntryStyle.COMPONENT,
      component: (
        <div className="flex gap-1">
          <ClipboardIcon
            onClick={() => {
              navigator.clipboard.writeText(report.report_uuid);
            }}
            className="w-4 cursor-pointer"
          />
          <span className="w-16 h-[30px] truncate">{report.report_uuid}</span>
        </div>
      ),
    },
    {
      text: `${report.client_report_id}`,
      style: ReportEntryStyle.TEXT,
    },
  ];

  if (canCancelMisconduct) {
    data.push({
      text: "Cancel",
      style: ReportEntryStyle.COMPONENT,
      component: <MisconductCancelButton reportUUID={report.report_uuid} />,
    });
  }

  return {
    report_uuid: report.report_uuid,
    report,
    isLocked: report.is_locked,
    data,
  };
}

/**
 * Table rows for Internal Investigator when data comes from ``GET /lists/misconduct/internal_qa``.
 * Mirrors ``InternalQALazyRow`` ``ReportSummaryItemApiToRow`` layout.
 */
export function buildInternalIqaV2026TableRows(
  summaries: ReportSummaryItemApi[] | undefined,
  opts: InternalIqaV2026RowOpts
): ReportRowItem<ReportSummaryItemApi>[] {
  if (!summaries) return [];
  return summaries.map((report) => buildInternalIqaV2026TableRow(report, opts));
}
